import { useTranslation } from 'react-i18next';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useRootSelector } from '../../../store/hooks';
import {
  selectPageConfig,
  TenantDomainId,
} from '../../../store/features/pageConfig';
import {
  selectSiteArticleDetailsData,
  TurtlPosition,
} from '../../../store/features/siteArticles/siteArticleDetails';
import { generateDomainUrl } from '../../../helpers/generateDomainUrl';
import { replaceImagePath } from '../../hub/audienceHub/pages/Article/ArticleHome/ArticleContent/replaceImagePath';
import Image from '../../hub/shared/productFilter/cards/ArticleBlogCard/components/Image';
import { renderHTMLString } from '../../../helpers';
import Topics from '../../hub/shared/productFilter/cards/ArticleBlogCard/components/Topics';
import { CardAnchor } from '../../hub/shared/productFilter/cards/components';
import Title from '../../hub/shared/productFilter/cards/ArticleBlogCard/components/Title';
import Description from '../../hub/shared/productFilter/cards/ArticleBlogCard/components/Description';
import ProductBlogCard from '../../hub/shared/productFilter/products/cardBuilders/ProductBlogCard';
import { CallToAction } from '../../shared/CallToAction/CallToAction';
import TurtlEmbed from '../../shared/TurtlEmbed';
import imageCover from '../../../assets/images/default-image.png';

import styles from './ArticleDetails.module.scss';

export default function ArticleDetails() {
  const { t } = useTranslation();
  const {
    sitePath,
    location: { href },
    location,
    tenantsConfig,
  } = useRootSelector(selectPageConfig);
  const articleData = useRootSelector(selectSiteArticleDetailsData);
  const {
    title,
    content,
    imageUrl,
    author: { name: authorName } = {},
    tags,
    relatedArticles,
    uploadedPdfUrls,
    turtl,
    turtl: { storyPosition: turtlPosition } = {},
  } = articleData;

  async function handleClickShareButton() {
    try {
      navigator.clipboard.writeText(href);
      await navigator.share({ url: href });
    } catch (err) {
      return;
    }
  }

  function generateArticleDownloadPdfUrl(pdfFileName: string) {
    const domainUploadUrl = generateDomainUrl({
      domains: tenantsConfig,
      tenant: TenantDomainId.INFORMACONNECT,
      path: 'uploads',
    });

    return `${domainUploadUrl}${pdfFileName}`;
  }

  return (
    <div className="container">
      <div className={styles.articleWrapper}>
        <div className={styles.articleTitle}>{title}</div>
        <div className={styles.articleAuthorDetails}>
          <span>
            {!!authorName && (
              <>
                {t('site.article.by')} <strong>{authorName}</strong>
              </>
            )}
          </span>
          <span
            className={styles.articleShareButton}
            onClick={handleClickShareButton}
          >
            {t('site.article.share.button')}
            <i className="material-icons">share</i>
          </span>
        </div>
        {uploadedPdfUrls && uploadedPdfUrls.length > 0 && (
          <div className={styles.articleDownloadSection}>
            {uploadedPdfUrls.map((pdfFileName) => {
              const downloadLink = generateArticleDownloadPdfUrl(pdfFileName);

              return (
                <CallToAction
                  key={downloadLink}
                  type="link"
                  target="_blank"
                  to={downloadLink}
                  className={styles.articleDownloadButton}
                >
                  <>
                    {t('site.article.download.button')}
                    <i className="material-icons">get_app</i>
                  </>
                </CallToAction>
              );
            })}
          </div>
        )}
        <div className={styles.articleImage}>
          <Image
            path={imageUrl}
            defaultImage={imageCover}
            imageStyle="featured"
          />
        </div>
        {turtlPosition === TurtlPosition.TOP && <TurtlEmbed turtl={turtl} />}
        <div className={styles.articleContent}>
          {renderHTMLString(replaceImagePath(content), location)}
        </div>
        {turtlPosition === TurtlPosition.BOTTOM && <TurtlEmbed turtl={turtl} />}
        {tags && (
          <div className={styles.articleTopics}>
            <Topics list={tags} />
          </div>
        )}
        {relatedArticles.length > 0 && (
          <>
            <h3 className={styles.relatedArticleHeading}>
              {t('site.article.related.news.title')}
            </h3>
            <div className="row">
              {relatedArticles.map((relatedArticle) => {
                const {
                  articleId,
                  imageUrl: relatedArticleImageUrl,
                  articlePath,
                  summary,
                  primaryTopic,
                  title: relatedArticleTitle,
                  externalLinkEnabled,
                  externalLink,
                } = relatedArticle;
                const path = externalLinkEnabled
                  ? externalLink
                  : `/article/${articlePath}`;

                return (
                  <div className="col-xs-12 col-sm-6 col-md-3" key={articleId}>
                    <div className={styles.relatedArticleCard}>
                      <CardAnchor to={path} pathPrefix={sitePath}>
                        <ProductBlogCard
                          image={<Image path={relatedArticleImageUrl} />}
                          topics={
                            primaryTopic && <Topics list={[primaryTopic]} />
                          }
                          heading={
                            <Title>
                              {relatedArticleTitle}{' '}
                              {externalLinkEnabled && (
                                <OpenInNewIcon
                                  fontSize="inherit"
                                  sx={{ verticalAlign: 'middle' }}
                                />
                              )}
                            </Title>
                          }
                          description={
                            summary && <Description>{summary}</Description>
                          }
                        />
                      </CardAnchor>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
